import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Droplet, Activity, Moon, Dumbbell, Heart, Brain, 
  Ban, Carrot, Pencil, Target, Sparkles, Smartphone,
  Egg, Check, Flame, Trophy, Zap, TrendingUp
} from 'lucide-react';
import { getDailyChallenges } from '../data/motivationalData';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const iconMap = {
  droplet: Droplet,
  activity: Activity,
  moon: Moon,
  dumbbell: Dumbbell,
  heart: Heart,
  brain: Brain,
  ban: Ban,
  carrot: Carrot,
  pencil: Pencil,
  target: Target,
  sparkles: Sparkles,
  'smartphone-off': Smartphone,
  egg: Egg,
  beef: Flame
};

const categoryColors = {
  training: 'bg-aura-primary',
  nutrition: 'bg-green-500',
  recovery: 'bg-cyan-500',
  mental: 'bg-purple-500'
};

const DailyChallenges = () => {
  const [challenges, setChallenges] = useState([]);
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0, total_days: 0 });
  const [loading, setLoading] = useState(true);
  const [allCompleteAnimation, setAllCompleteAnimation] = useState(false);

  useEffect(() => {
    const dailyChallenges = getDailyChallenges();
    setChallenges(dailyChallenges);
    fetchCompletedChallenges();
  }, []);

  const fetchCompletedChallenges = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/challenges/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletedChallenges(response.data.completed || []);
      if (response.data.streak) {
        setStreak(response.data.streak);
      }
    } catch (error) {
      // Endpoint might not exist yet, use empty array
      setCompletedChallenges([]);
    } finally {
      setLoading(false);
    }
  };

  const completeChallenge = async (challengeId) => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/challenges/complete`, 
        { challenge_id: challengeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCompletedChallenges(response.data.completed || [...completedChallenges, challengeId]);
      
      if (response.data.streak) {
        setStreak(response.data.streak);
      }
      
      const challenge = challenges.find(c => c.id === challengeId);
      toast.success(`Challenge complete! +${challenge?.points || 10} Aura points`);
      
      // Check if all challenges are now complete
      if (response.data.all_complete) {
        setAllCompleteAnimation(true);
        setTimeout(() => setAllCompleteAnimation(false), 3000);
        
        // Show streak celebration
        if (response.data.streak?.current_streak > 1) {
          setTimeout(() => {
            toast.success(`🔥 ${response.data.streak.current_streak} Day Streak! +${Math.min(response.data.streak.current_streak * 5, 50)} bonus points!`);
          }, 500);
        }
      }
    } catch (error) {
      // If endpoint doesn't exist, just update UI
      setCompletedChallenges(prev => [...prev, challengeId]);
      const challenge = challenges.find(c => c.id === challengeId);
      toast.success(`Challenge complete! +${challenge?.points || 10} Aura points`);
    }
  };

  const completedCount = challenges.filter(c => completedChallenges.includes(c.id)).length;
  const totalPoints = challenges
    .filter(c => completedChallenges.includes(c.id))
    .reduce((sum, c) => sum + c.points, 0);

  // Calculate streak bonus (shown when all complete)
  const streakBonus = completedCount === 3 ? Math.min(streak.current_streak * 5, 50) : 0;

  return (
    <Card className={`bg-aura-surface border-white/10 transition-all ${allCompleteAnimation ? 'ring-2 ring-yellow-400 ring-opacity-50' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Daily Challenges
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Streak Badge */}
            {streak.current_streak > 0 && (
              <Badge className="bg-orange-500/20 text-orange-400 border-0 flex items-center gap-1" data-testid="streak-badge">
                <Flame className="w-3 h-3" />
                {streak.current_streak} Day Streak
              </Badge>
            )}
            <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
              {completedCount}/3 Done
            </Badge>
          </div>
        </div>
        <Progress 
          value={(completedCount / 3) * 100} 
          className="h-2 mt-2" 
        />
      </CardHeader>
      <CardContent className="space-y-3">
        {challenges.map((challenge) => {
          const Icon = iconMap[challenge.icon] || Target;
          const isCompleted = completedChallenges.includes(challenge.id);
          
          return (
            <div 
              key={challenge.id}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                isCompleted 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
              data-testid={`challenge-${challenge.id}`}
            >
              <div className={`w-10 h-10 rounded-lg ${categoryColors[challenge.category]} bg-opacity-20 flex items-center justify-center`}>
                <Icon className={`w-5 h-5 ${isCompleted ? 'text-green-400' : 'text-white'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`font-medium ${isCompleted ? 'text-green-400 line-through' : 'text-white'}`}>
                    {challenge.title}
                  </p>
                  <Badge className={`text-xs ${categoryColors[challenge.category]} bg-opacity-20 border-0 capitalize`}>
                    {challenge.category}
                  </Badge>
                </div>
                <p className="text-sm text-aura-text-muted">{challenge.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-yellow-400 font-medium">+{challenge.points}</span>
                {isCompleted ? (
                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => completeChallenge(challenge.id)}
                    className="bg-aura-primary hover:bg-aura-primary-hover text-white h-8 w-8 p-0"
                    data-testid={`complete-${challenge.id}`}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {completedCount === 3 && (
          <div className={`text-center py-3 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg border border-yellow-500/30 transition-all ${allCompleteAnimation ? 'scale-105' : ''}`}>
            <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <p className="font-heading font-bold text-yellow-400 uppercase">All Challenges Complete!</p>
            <p className="text-sm text-aura-text-muted">
              +{totalPoints} Aura points earned today
              {streakBonus > 0 && (
                <span className="text-orange-400"> (+{streakBonus} streak bonus!)</span>
              )}
            </p>
          </div>
        )}

        {/* Streak Stats */}
        {(streak.longest_streak > 0 || streak.total_days > 0) && (
          <div className="flex items-center justify-between pt-2 border-t border-white/10">
            <div className="flex items-center gap-4 text-xs text-aura-text-muted">
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-aura-secondary" />
                <span>Best: {streak.longest_streak} days</span>
              </div>
              <div className="flex items-center gap-1">
                <Trophy className="w-3 h-3 text-yellow-400" />
                <span>Total: {streak.total_days} days</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DailyChallenges;
